import React, { useState, useEffect } from 'react';
import VoiceIntakeForms from '../../components/VoiceIntakeForms';

/**
 * Standalone demo page for the Voice Intake Forms component
 * This allows testing the voice intake feature without going through the full check-in flow
 */
const VoiceIntakeDemo = () => {
  const [submittedData, setSubmittedData] = useState(null);
  const [activeTab, setActiveTab] = useState('responses');
  
  // Mock token data for testing
  const mockTokenData = {
    patient_id: 'demo-patient-123',
    first_name: 'Demo',
    last_name: 'Patient',
    doctor_name: 'Dr. Smith',
    state: 'demo'
  };
  
  // Handle form submission
  const handleSuccess = (data) => {
    console.log('Form submitted with data:', data);
    setSubmittedData(data);
  };

  
  return (
    <div className="container py-5">
      <div className="row mb-4">
        <div className="col-12">
          <h1 className="text-center">Voice Intake Demo</h1>
          <p className="text-center text-muted">
            This is a standalone demo of the voice intake forms feature using LemonFox AI Speech-to-Text API.
            You can test the voice recording and form submission without going through the full check-in flow.
          </p>
        </div>
      </div>
      
      {submittedData ? (
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header">
                <h3 className="mb-0">Intake Form submitted successfully!</h3>
              </div>
              <div className="card-body">
                <p className="lead">Thank you for completing the voice intake form. Here&apos;s a summary of your responses:</p>
                
                {/* Tabs for different views */}
                <ul className="nav nav-tabs mb-4" id="resultTabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'responses' ? 'active' : ''}`}
                      onClick={() => setActiveTab('responses')}
                      type="button"
                    >
                      <i className="fas fa-comment-alt me-2"></i>
                      Your Responses
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                      onClick={() => setActiveTab('details')}
                      type="button"
                      disabled={!submittedData.transcription_details}
                    >
                      <i className="fas fa-file-alt me-2"></i>
                      Transcription Details
                    </button>
                  </li>
                </ul>
                {/* Responses Tab */}
                {activeTab === 'responses' && (
                  <div className="tab-content">
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <h5 className="mb-0">Why do you want to talk to the provider today?</h5>
                      </div>
                      <div className="card-body">
                        <p>{submittedData.reasonForVisit || 'No response provided'}</p>
                      </div>
                    </div>
                    
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <h5 className="mb-0">Tell us more about the main symptoms you are having?</h5>
                      </div>
                      <div className="card-body">
                        <p>{submittedData.symptoms || 'No response provided'}</p>
                      </div>
                    </div>
                    
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <h5 className="mb-0">Do you have any changes to medications or allergies since your last visit?</h5>
                      </div>
                      <div className="card-body">
                        <p>{submittedData.medicationsOrAllergies || 'No response provided'}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Transcription Details Tab */}
                {activeTab === 'details' && submittedData.transcription_details && (
                  <div className="tab-content">
                    <div className="accordion" id="transcriptionAccordion">
                      {Object.keys(submittedData.transcription_details).map((key, index) => {
                        const details = submittedData.transcription_details[key];
                        if (!details) return null;
                        
                        const questionId = key.replace('_details', '');
                        const questionObj = [
                          { id: 'reasonForVisit', title: 'Reason for Visit' },
                          { id: 'symptoms', title: 'Symptoms' },
                          { id: 'medicationsOrAllergies', title: 'Medications or Allergies' }
                        ].find(q => q.id === questionId);
                        
                        return (
                          <div className="accordion-item" key={index}>
                            <h2 className="accordion-header" id={`heading${index}`}>
                              <button
                                className="accordion-button"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#collapse${index}`}
                                aria-expanded="true"
                                aria-controls={`collapse${index}`}
                              >
                                <span>{questionObj ? questionObj.title : key}</span>
                              </button>
                            </h2>
                            <div
                              id={`collapse${index}`}
                              className="accordion-collapse collapse show"
                              aria-labelledby={`heading${index}`}
                              data-bs-parent="#transcriptionAccordion"
                            >
                              <div className="accordion-body">
                                <div className="card mb-3 bg-light">
                                  <div className="card-body">
                                    <h6 className="card-subtitle mb-2 text-muted">Transcription Summary</h6>
                                    <p className="card-text"><strong>Full Text:</strong> {details.text}</p>
                                    <div className="row mt-3">
                                      <div className="col-md-6">
                                        <p><strong>Language:</strong> {details.language}</p>
                                      </div>
                                      <div className="col-md-6">
                                        <p><strong>Duration:</strong> {details.duration.toFixed(1)} seconds</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {details.segments && details.segments.length > 0 && (
                                  <div>
                                    <h6 className="mb-3">Speech Segments</h6>
                                    <div className="table-responsive">
                                      <table className="table table-striped table-bordered">
                                        <thead className="table-light">
                                          <tr>
                                            <th>Segment</th>
                                            <th>Text</th>
                                            <th>Time Range</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {details.segments.map((segment, i) => (
                                            <tr key={i}>
                                              <td>{i + 1}</td>
                                              <td>{segment.text}</td>
                                              <td>{segment.start.toFixed(1)}s - {segment.end.toFixed(1)}s</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                                
                                {details.words && details.words.length > 0 && (
                                  <div className="mt-4">
                                    <h6 className="mb-3">Word-Level Analysis</h6>
                                    <div className="table-responsive">
                                      <table className="table table-sm table-hover">
                                        <thead className="table-light">
                                          <tr>
                                            <th>Word</th>
                                            <th>Start</th>
                                            <th>End</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {details.words.map((word, i) => (
                                            <tr key={i}>
                                              <td>{word.word}</td>
                                              <td>{word.start.toFixed(2)}s</td>
                                              <td>{word.end.toFixed(2)}s</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Analytics Tab */}
                {activeTab === 'analytics' && submittedData.transcription_details && (
                  <div className="tab-content">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="card mb-3">
                          <div className="card-header bg-light">
                            <h5 className="mb-0">Transcription Quality</h5>
                          </div>
                          <div className="card-body">
                            <div className="mb-4">
                              <h6>Overall Confidence Scores</h6>
                              {Object.keys(submittedData.transcription_details).map((key, index) => {
                                const details = submittedData.transcription_details[key];
                                if (!details || !details.segments) return null;
                                
                                const questionId = key.replace('_details', '');
                                const questionObj = [
                                  { id: 'reasonForVisit', title: 'Reason for Visit' },
                                  { id: 'symptoms', title: 'Symptoms' },
                                  { id: 'medicationsOrAllergies', title: 'Medications or Allergies' }
                                ].find(q => q.id === questionId);
                                
                                // const avgConfidence = getAverageConfidence(details.segments);
                                
                                // return (
                                //   <div key={index} className="mb-3">
                                //     <div className="d-flex justify-content-between mb-1">
                                //       <span>{questionObj ? questionObj.title : key}</span>
                                //       <span className={getConfidenceClass(avgConfidence)}>
                                //         {formatConfidence(avgConfidence)}
                                //       </span>
                                //     </div>
                                //     <div className="progress">
                                //       <div
                                //         className={`progress-bar ${avgConfidence < 0.7 ? 'bg-danger' : avgConfidence < 0.9 ? 'bg-warning' : 'bg-success'}`}
                                //         role="progressbar"
                                //         style={{width: `${avgConfidence * 100}%`}}
                                //         aria-valuenow={avgConfidence * 100}
                                //         aria-valuemin="0"
                                //         aria-valuemax="100"
                                //       ></div>
                                //     </div>
                                //   </div>
                                // );
                              })}
                            </div>
                            
                            <div className="alert alert-info">
                              <i className="fas fa-info-circle me-2"></i>
                              <strong>Understanding Confidence Scores:</strong>
                              <ul className="mb-0 mt-2">
                                <li><span className="text-success">90-100%</span>: High confidence, very accurate</li>
                                <li><span className="text-warning">70-89%</span>: Medium confidence, generally accurate</li>
                                <li><span className="text-danger">Below 70%</span>: Low confidence, may contain errors</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="card mb-3">
                          <div className="card-header bg-light">
                            <h5 className="mb-0">Speech Metrics</h5>
                          </div>
                          <div className="card-body">
                            {Object.keys(submittedData.transcription_details).map((key, index) => {
                              const details = submittedData.transcription_details[key];
                              if (!details) return null;
                              
                              const questionId = key.replace('_details', '');
                              const questionObj = [
                                { id: 'reasonForVisit', title: 'Reason for Visit' },
                                { id: 'symptoms', title: 'Symptoms' },
                                { id: 'medicationsOrAllergies', title: 'Medications or Allergies' }
                              ].find(q => q.id === questionId);
                              
                              const wordCount = details.text.split(' ').length;
                              const wordsPerSecond = wordCount / details.duration;
                              const segmentCount = details.segments ? details.segments.length : 0;
                              
                              return (
                                <div key={index} className="mb-4">
                                  <h6>{questionObj ? questionObj.title : key}</h6>
                                  <div className="row">
                                    <div className="col-6 col-md-6 mb-3">
                                      <div className="card bg-light h-100">
                                        <div className="card-body text-center">
                                          <h3 className="mb-0">{wordCount}</h3>
                                          <p className="text-muted mb-0">Words</p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-6 col-md-6 mb-3">
                                      <div className="card bg-light h-100">
                                        <div className="card-body text-center">
                                          <h3 className="mb-0">{details.duration.toFixed(1)}s</h3>
                                          <p className="text-muted mb-0">Duration</p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-6 col-md-6 mb-3">
                                      <div className="card bg-light h-100">
                                        <div className="card-body text-center">
                                          <h3 className="mb-0">{wordsPerSecond.toFixed(1)}</h3>
                                          <p className="text-muted mb-0">Words/Second</p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-6 col-md-6 mb-3">
                                      <div className="card bg-light h-100">
                                        <div className="card-body text-center">
                                          <h3 className="mb-0">{segmentCount}</h3>
                                          <p className="text-muted mb-0">Segments</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <button
                  className="btn btn-primary mt-4"
                  onClick={() => setSubmittedData(null)}
                >
                  <i className="fas fa-sync me-2"></i>
                  Start Over
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <VoiceIntakeForms 
              tokenData={mockTokenData}
              onSuccess={handleSuccess}
              checkInData={{}}
            />
          </div>
        </div>
      )}
      
      <div className="row mt-5">
        <div className="col-12">
          <div className="card bg-light shadow-sm">
            <div className="card-header bg-light">
              <h4 className="mb-0">How to Use</h4>
            </div>
            <div className="card-body">
              <ol>
                <li>Click the microphone button to start recording your response</li>
                <li>Speak clearly into your microphone</li>
                <li>Click the microphone button again to stop recording</li>
                <li>Wait for the LemonFox AI to transcribe your speech</li>
                <li>Review your response by clicking &quot;View Response&quot;</li>
                <li>If needed, you can edit your response manually</li>
                <li>Click &quot;Next&quot; to proceed to the next question</li>
                <li>After answering all questions, click &quot;Submit&quot; to complete the form</li>
              </ol>
              
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Note:</strong> This demo uses the LemonFox AI Speech-to-Text API for high-quality transcription.
                The API provides detailed information about the transcription, including word-level timing and language detection.
              </div>
              
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-light">
              <h4 className="mb-0">About LemonFox AI Speech-to-Text</h4>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-7">
                  <p>
                    The LemonFox Speech-to-Text API converts spoken language into written text with high accuracy.
                    It uses advanced AI models to handle various accents, background noise, and specialized terminology.
                  </p>
                  <h5 className="mt-4">Key Features</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <ul>
                        <li><strong>High Accuracy:</strong> State-of-the-art speech recognition</li>
                        <li><strong>Multi-language Support:</strong> Recognizes multiple languages</li>
                        <li><strong>Noise Resilience:</strong> Works well in noisy environments</li>
                        <li><strong>Medical Terminology:</strong> Optimized for healthcare vocabulary</li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <ul>
                        <li><strong>Detailed Analytics:</strong> Confidence scores and timing</li>
                        <li><strong>Fast Processing:</strong> Near real-time transcription</li>
                        <li><strong>Secure:</strong> HIPAA-compliant processing</li>
                        <li><strong>Easy Integration:</strong> Simple REST API</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-md-5">
                  <div className="card bg-light">
                    <div className="card-header">
                      <h5 className="mb-0">API Response Format</h5>
                    </div>
                    <div className="card-body">
                      <p className="small">The API returns detailed information in verbose_json format:</p>
                      <ul className="small">
                        <li>Full transcription text</li>
                        <li>Segmented phrases with timestamps</li>
                        <li>Word-level timing information</li>
                        <li>Language detection</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceIntakeDemo;